import { CompanySizes, GetOrganizationListRequestQuery, Industries, Prefectures } from '@engagement-web-app/common';
import { uuidSchema } from '@engagement-web-app/common/validation-utils';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { InvalidRequest } from '@/components/InvalidRequest';
import { Page } from '@/components/Page';
import { Pagination } from '@/components/Pagination';
import { Pill } from '@/components/Pill';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { debounce, getFullName } from '@/utils';
import { CreateOrganizationDialog } from './-fragments/CreateOrganizationDialog';
import { OrganizationCard, OrganizationCardSkeleton } from './-fragments/OrganizationCard';
import { OrganizationFilter } from './-fragments/OrganizationFilter';
import { useCompanySizes, useIndustries, useOrganizations, usePrefectures, useSystemUsers } from './-hooks';
import * as styles from './index.css';

const MAX_VISIBLE_FILTER_PILLS = 10;

const DefaultRouteQueryParams = {
    from: 0,
    limit: 6,
    search: '',
    assignedConsultantIds: [],
    industryIds: [],
    prefectureIds: [],
    companySizeIds: [],
};

const RouteQueryParams = z.object({
    from: GetOrganizationListRequestQuery.shape.from.default(DefaultRouteQueryParams.from),
    limit: GetOrganizationListRequestQuery.shape.limit.default(DefaultRouteQueryParams.limit),
    search: GetOrganizationListRequestQuery.shape.search.default(DefaultRouteQueryParams.search),
    assignedConsultantIds: z.array(uuidSchema).default(DefaultRouteQueryParams.assignedConsultantIds),
    industryIds: z.array(Industries).default(DefaultRouteQueryParams.industryIds),
    prefectureIds: z.array(Prefectures).default(DefaultRouteQueryParams.prefectureIds),
    companySizeIds: z.array(CompanySizes).default(DefaultRouteQueryParams.companySizeIds),
});

type RouteQueryParams = z.infer<typeof RouteQueryParams>;

export const Route = createFileRoute('/system/_auth/organizations/')({
    component: SystemOrganizationListRoute,
    validateSearch: zodValidator(RouteQueryParams),
    search: {
        middlewares: [stripSearchParams(DefaultRouteQueryParams)],
    },
    errorComponent: () => <InvalidRequest title="クライアント" kind="system" />,
});

function SystemOrganizationListRoute() {
    const { from, limit, search, assignedConsultantIds, industryIds, prefectureIds, companySizeIds } =
        Route.useSearch();
    const navigate = Route.useNavigate();
    const [searchText, setSearchText] = useState(search);

    const queryParams = useMemo<RouteQueryParams>(() => {
        return {
            from,
            limit,
            search,
            assignedConsultantIds,
            industryIds,
            prefectureIds,
            companySizeIds,
        };
    }, [from, limit, search, assignedConsultantIds, industryIds, prefectureIds, companySizeIds]);

    const applyQuery = useCallback(
        (newQueryParams: Partial<RouteQueryParams>) => {
            navigate({
                from: Route.fullPath,
                search: { ...queryParams, ...newQueryParams },
            });
        },
        [navigate, queryParams]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Required to properly apply query params
    const debouncedTextSearch = useCallback(
        debounce((text: string) => {
            applyQuery({ ...queryParams, search: text, from: 0 });
        }, 300),
        [queryParams]
    );

    const {
        query: {
            isFetching: isOrganizationsFetching,
            isError: isOrganizationsError,
            refetch: refetchOrganizations,
            data: organizationsData,
        },
        lastPage,
    } = useOrganizations(from, limit, search, assignedConsultantIds, industryIds, prefectureIds, companySizeIds);

    const systemUsersQuery = useSystemUsers();
    const industriesQuery = useIndustries();
    const prefecturesQuery = usePrefectures();
    const companySizesQuery = useCompanySizes();

    const hasAnyFilters =
        searchText.trim() !== '' ||
        !!assignedConsultantIds.length ||
        !!industryIds.length ||
        !!prefectureIds.length ||
        !!companySizeIds.length;

    const appliedFilters = useMemo(() => {
        return [
            ...selectedFilters(
                'コンサルタント担当者',
                systemUsersQuery.data?.map((user) => ({ id: user.uuid, name: getFullName(user) })),
                assignedConsultantIds
            ),
            ...selectedFilters(
                '業界',
                industriesQuery.data?.map((industry) => ({ id: industry.id, name: industry.displayName })),
                industryIds
            ),
            ...selectedFilters(
                '都道府県',
                prefecturesQuery.data?.map((prefecture) => ({ id: prefecture.id, name: prefecture.displayName })),
                prefectureIds
            ),
            ...selectedFilters(
                '従業員数',
                companySizesQuery.data?.map((companySize) => ({ id: companySize.id, name: companySize.displayName })),
                companySizeIds
            ),
        ];
    }, [
        systemUsersQuery.data,
        industriesQuery.data,
        prefecturesQuery.data,
        companySizesQuery.data,
        assignedConsultantIds,
        industryIds,
        prefectureIds,
        companySizeIds,
    ]);

    const visibleFilters = appliedFilters.slice(0, MAX_VISIBLE_FILTER_PILLS);
    const hiddenFilterCount = appliedFilters.length - visibleFilters.length;

    return (
        <Page.Root>
            <Page.Header title="クライアント">
                {organizationsData ? (
                    <Pill>{`${organizationsData?.total}社`}</Pill>
                ) : (
                    <Skeleton.Line width={50} height="xl" />
                )}
            </Page.Header>

            <Page.Content>
                <div className={styles.searchRow}>
                    <SearchInput
                        placeholder="検索"
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            debouncedTextSearch(e.target.value);
                        }}
                    />
                    <CreateOrganizationDialog />
                </div>

                <Flex direction="column" gap="4" align="start">
                    <OrganizationFilter
                        value={{
                            assignedConsultantIds,
                            industryIds,
                            prefectureIds,
                            companySizeIds,
                        }}
                        systemUsers={systemUsersQuery}
                        industries={industriesQuery}
                        prefectures={prefecturesQuery}
                        companySizes={companySizesQuery}
                        onApply={(value) => applyQuery({ ...value, from: 0 })}
                    />

                    {visibleFilters.length > 0 && (
                        <Flex gap="2" wrap="wrap">
                            {visibleFilters.map((filter) => (
                                <Pill variant="quinary" key={filter.key}>
                                    {filter.label}
                                </Pill>
                            ))}
                            {/* TODO: Hovering the "+<count>" pill to reveal all "hidden" filters would be a nice to have? */}
                            {hiddenFilterCount > 0 && <Pill variant="quinary">+{hiddenFilterCount}</Pill>}
                        </Flex>
                    )}
                </Flex>

                <ErrorOverlay active={isOrganizationsError} onRetry={refetchOrganizations}>
                    {!organizationsData && <OrganizationCardListSkeleton limit={limit} />}

                    {organizationsData && (
                        <Flex as={Skeleton.Overlay} active={isOrganizationsFetching} direction="column" gap="6">
                            <p className={styles.countText}>クライアント数：{organizationsData.count}社</p>

                            {organizationsData.list.length > 0 ? (
                                <ul className={styles.cardGrid}>
                                    {organizationsData.list.map((org) => (
                                        <li key={org.uuid}>
                                            <OrganizationCard org={org} />
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                // TODO: Visual design for empty state
                                // TODO: Prompt user to create first organization?
                                <p className={styles.emptyState}>
                                    {hasAnyFilters
                                        ? '検索条件に一致する企業はありません。'
                                        : '現在、企業は登録されていません。'}
                                </p>
                            )}
                        </Flex>
                    )}
                </ErrorOverlay>

                {lastPage !== null && (
                    <Flex justify="center">
                        <Pagination
                            currentPage={from / limit + 1}
                            lastPage={lastPage}
                            onPageChange={(page) =>
                                navigate({
                                    from: Route.fullPath,
                                    search: { ...queryParams, from: (page - 1) * limit },
                                })
                            }
                        />
                    </Flex>
                )}
            </Page.Content>
        </Page.Root>
    );
}

function selectedFilters(
    groupName: string,
    options: { id: string; name: string }[] | undefined,
    selectedIds: string[]
) {
    if (!options) return [];
    return options
        .filter((option) => selectedIds.includes(option.id))
        .map((option) => ({ key: `${groupName}:${option.id}`, label: `${groupName}：${option.name}` }));
}

function OrganizationCardListSkeleton({ limit }: { limit: number }) {
    return (
        <>
            <Skeleton.Line width={120} height="xs" />

            <ul className={styles.cardGrid}>
                {Array.from({ length: limit }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, cards have no identity
                    <li key={i}>
                        <OrganizationCardSkeleton />
                    </li>
                ))}
            </ul>
        </>
    );
}
